import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { Button, Badge } from "@tellescope/ui";
import { formatDateTime } from "@tellescope/utils";
import type { FormResponse } from "@tellescope/types-models";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useIframeToken } from "../../hooks/useIframeToken";
import { useOrganization } from "../../../shared/store/organizationStore";
import { buildFormCompletionURL } from "../../../config/portal";
import { renderAnswer } from "../../../shared/forms";

interface FormAnswerProps {
  title: string;
  answer: NonNullable<FormResponse["responses"]>[number]["answer"];
  answerIsHTML?: boolean;
}

const FormAnswer = ({ title, answer, answerIsHTML }: FormAnswerProps) => {
  const renderedAnswer = renderAnswer(answer, answerIsHTML);
  if (!renderedAnswer) return null;

  return (
    <div className="border-b border-border pb-3 last:border-b-0">
      <div className="text-sm font-medium text-muted-foreground mb-1">{title}</div>
      <div className="text-sm text-foreground">{renderedAnswer}</div>
    </div>
  );
};

/**
 * Legacy form response view page that handles direct form response URLs.
 * Maps to old portal's /documents/form-responses/:id route.
 *
 * URL params:
 * - id: the form response ID
 */
export const FormResponseViewPage = () => {
  const { t } = useTranslation("forms");
  const { id } = useParams<{ id: string }>();
  const organization = useOrganization();

  // Fetch form response using TanStack Query
  const {
    data: formResponse,
    isLoading: isLoadingResponse,
    error: fetchError,
  } = useTellescopeQuery({
    model: "form_responses",
    method: "getOne",
    request: id ?? "",
    enabled: !!id,
  });

  // Fetch iframe token for pending forms using TanStack Query hook
  const { token: iframeToken, isLoading: isLoadingToken } = useIframeToken({
    enabled: !!formResponse && !formResponse.submittedAt,
  });

  const isSubmitted = !!formResponse?.submittedAt;
  const hasDraft = !!formResponse?.draftSavedAt;
  const isPending = formResponse && !isSubmitted;

  // Filter responses to only show those with sharedWithEnduser flag or all for submitted forms
  const visibleResponses = formResponse?.responses?.filter((response) => {
    return response.sharedWithEnduser === true || isSubmitted;
  });

  // Build the iframe URL for pending forms
  const formCompletionURL =
    isPending && formResponse && organization && iframeToken && formResponse.accessCode
      ? buildFormCompletionURL({
          organization: { subdomain: organization.subdomain },
          formId: formResponse.formId,
          accessCode: formResponse.accessCode,
          authToken: iframeToken,
          automationStepId: formResponse.automationStepId,
          hideBg: true,
          startAtNext: true,
        })
      : null;

  if (isLoadingResponse) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading form...")}</p>
        </div>
      </div>
    );
  }

  if (fetchError || !formResponse) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-sm font-medium text-destructive mb-1">{t("Unable to load form")}</p>
          <p className="text-xs text-muted-foreground mb-4">{fetchError?.message ?? t("Form not found")}</p>
          <Link to="/documents">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("Go Back")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Pending form - show iframe for completion
  if (isPending) {
    if (isLoadingToken) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center text-muted-foreground">
            <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
            <p className="text-sm">{t("Loading form...")}</p>
          </div>
        </div>
      );
    }

    if (formCompletionURL) {
      return (
        <div className="h-screen flex flex-col">
          {/* Header with back button */}
          <div className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0">
            <Link to="/documents">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="text-base font-medium truncate">{formResponse.formTitle}</h1>
          </div>

          {/* Form iframe */}
          <iframe
            src={formCompletionURL}
            className="flex-1 w-full border-0"
            title={formResponse.formTitle}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      );
    }

    // Missing data to build URL (token fetch failed or missing accessCode)
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/documents">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold truncate">{formResponse.formTitle}</h1>
        </div>

        <div className="bg-background rounded-lg border p-8">
          <div className="flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 text-amber-600" />
              <p className="text-sm font-medium mb-1">{hasDraft ? t("Draft saved") : t("Awaiting completion")}</p>
              <p className="text-xs">
                {hasDraft && formResponse.draftSavedAt
                  ? formatDateTime(formResponse.draftSavedAt)
                  : t("This form is ready to be completed")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Submitted form - show responses
  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/documents">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold truncate">{formResponse.formTitle}</h1>
      </div>

      {/* Content */}
      <div className="bg-background rounded-lg border overflow-hidden">
        {/* Metadata section */}
        <div className="px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>{t("Submitted {{timestamp}}", { timestamp: formatDateTime(formResponse.submittedAt!) })}</span>
          </div>

          {/* Show scores if available */}
          {formResponse.scores && formResponse.scores.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formResponse.scores.map((score, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {score.title}: {score.value}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Responses */}
        <div className="p-4">
          {!visibleResponses || visibleResponses.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <p className="text-sm">{t("No responses recorded")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleResponses.map((response) => (
                <FormAnswer
                  key={response.fieldId}
                  title={response.fieldTitle}
                  answer={response.answer}
                  answerIsHTML={response.answerIsHTML}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
